// Shop short names in hub addresses (docs/SHOP-SHORT-URLS.md). A shop page's address carries a
// ref: normally the short name (/shop/aling-nena), but an old link may carry the id or a retired
// name. <ShopRefGate> turns the ref into the shop's id for the API calls and, when the ref isn't
// the canonical short name, replaces the address with it (same page, same query string), so
// bookmarks and links from before a rename keep working and the bar always shows the name.
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useMaybeAuth } from '../auth/AuthProvider';
import { ErrorState, Loading } from '../ui/bits';

export type ResolvedShop = { id: string; slug: string };

export const shopRefKey = (ref: string) => ['shop-ref', ref] as const;

/**
 * Resolve a ref. A short name this person's own roles already carry needs no request; anything
 * else (an admin opening any shop, an id, a retired name) asks GET /shop-ref/:ref once.
 */
export function useShopRef(ref: string) {
  const me = useMaybeAuth()?.me;
  const own = me?.roles.find((r) => r.shopSlug === ref && r.shopId);
  const q = useQuery({
    queryKey: shopRefKey(ref),
    queryFn: () => api.shopRef(ref),
    enabled: !!ref && !own,
    staleTime: 5 * 60_000,
    retry: false,
  });
  if (own?.shopId) return { shop: { id: own.shopId, slug: ref } as ResolvedShop, canonical: true, query: q };
  return { shop: q.data ? ({ id: q.data.id, slug: q.data.slug } as ResolvedShop) : null, canonical: q.data?.canonical ?? false, query: q };
}

/** The address with the ref segment after `prefix` swapped for the short name. */
export function canonicalPath(pathname: string, prefix: string, ref: string, slug: string) {
  const head = `${prefix}/${encodeURIComponent(ref)}`;
  const at = pathname.indexOf(head);
  if (at < 0) return `${prefix}/${encodeURIComponent(slug)}`;
  return `${pathname.slice(0, at)}${prefix}/${encodeURIComponent(slug)}${pathname.slice(at + head.length)}`;
}

export function ShopRefGate({ shopRef, prefix, children }: { shopRef: string; prefix: '/shop' | '/ops/shops' | '/ops/products'; children(shop: ResolvedShop): ReactNode }) {
  const loc = useLocation();
  const { shop, canonical, query } = useShopRef(shopRef);
  if (shop && !canonical && shop.slug !== shopRef) {
    return <Navigate replace to={{ pathname: canonicalPath(loc.pathname, prefix, shopRef, shop.slug), search: loc.search, hash: loc.hash }} />;
  }
  if (shop) return <>{children(shop)}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return <Loading label="Opening the shop" rows={2} />;
}
